"use client"

import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { LeoIcon } from "@/components/ui/leo-icon"
import { StatusBadge } from "@/components/ui/status-badge"
import { Tip } from "@/components/ui/tip"
import { BLUEPRINT_ICON } from "@/lib/blueprint/icons"
import { joinEnglishList, type PaperQuestionBlueprintExplain } from "@/lib/leo-blueprint-section-fill"

export function BlueprintAlignCell({
  explain,
}: {
  explain: PaperQuestionBlueprintExplain
}) {
  const whyLabel = explain.aligned
    ? "Why this item is aligned"
    : "Why this item is not aligned"

  return (
    <div
      className="flex min-w-0 items-center gap-1"
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <StatusBadge
        label={explain.title}
        tone={explain.aligned ? "success" : "info"}
        icon={explain.aligned ? "fa-circle-check" : BLUEPRINT_ICON}
        size="md"
      />
      <Popover modal={false}>
        <Tip label={whyLabel}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              aria-label={whyLabel}
              className="size-7 shrink-0 text-muted-foreground"
            >
              <i className="fa-light fa-circle-info" aria-hidden="true" />
            </Button>
          </PopoverTrigger>
        </Tip>
        <PopoverContent
          align="start"
          side="bottom"
          sideOffset={6}
          className="w-[min(20rem,calc(100vw-1.5rem))] p-0"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="border-b border-border px-3.5 py-3">
            <div className="flex items-center gap-2">
              <LeoIcon variant="ambient" size="xs" state="answered" />
              <p className="text-sm font-semibold leading-5 text-foreground">
                Leo spotted
              </p>
              <StatusBadge
                label={explain.title}
                tone={explain.aligned ? "success" : "info"}
                icon={explain.aligned ? "fa-circle-check" : BLUEPRINT_ICON}
                size="md"
              />
            </div>
            <p className="mt-2 text-sm leading-5 text-foreground">{explain.summary}</p>
          </div>
          {explain.checks.length > 0 ? (
            <dl className="space-y-3 px-3.5 py-3">
              {explain.checks.map((check) => (
                <div key={check.key} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-0.5">
                  <dt className="text-xs leading-4 text-muted-foreground">{check.label}</dt>
                  <dd className="row-span-2 m-0 flex items-center gap-1.5 self-start text-sm leading-5 text-foreground">
                    <i
                      className={
                        check.aligned
                          ? "fa-light fa-circle-check text-success"
                          : "fa-light fa-circle-xmark text-warning"
                      }
                      aria-hidden
                    />
                    {check.aligned ? "Aligned" : "Not aligned"}
                  </dd>
                  <dd className="m-0 min-w-0 text-sm font-medium leading-5 text-foreground">
                    {check.value}
                  </dd>
                  {!check.aligned ? (
                    <dd className="col-span-2 m-0 text-xs leading-4 text-muted-foreground">
                      Blueprint uses {joinEnglishList(check.plan)}.
                    </dd>
                  ) : null}
                </div>
              ))}
            </dl>
          ) : null}
        </PopoverContent>
      </Popover>
    </div>
  )
}
